// frontend/src/pages/Consultancies.js
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AdjustmentsIcon,
  FilterIcon,
  LocationMarkerIcon,
  SearchIcon,
  ShieldCheckIcon,
  TagIcon,
  XIcon,
} from "@heroicons/react/solid";
import { BACKEND_URL } from "../config";
import { getActiveOffers } from "../utils/offerUtils";
import ConsultancyCard from "../components/ConsultancyCard";
import SectionHeader from "../components/public/SectionHeader";
import { LoadingSkeleton, StatusBadge } from "../components/public/MarketplaceCards";
import { useAuth } from "../hooks/useAuth";

const sortOptions = [
  { value: "recommended", label: "Recommended" },
  { value: "verified_first", label: "Verified first" },
  { value: "most_complete", label: "Most complete" },
  { value: "alphabetical", label: "Alphabetical" },
  { value: "recently_verified", label: "Recently verified" },
];

const pageSize = 24;
const facetFallbackPageSize = 60;

function normalizedText(value = "") {
  return String(value).toLowerCase().replace(/\s+/g, " ").trim();
}

function normalizedKey(value = "") {
  return normalizedText(value).replace(/[^a-z0-9]/g, "");
}

function normalizedArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function uniqueSorted(items = [], { invalidValues = [] } = {}) {
  const invalidKeys = new Set(invalidValues.map(normalizedKey).filter(Boolean));
  const byKey = new Map();
  items
    .flatMap((item) => normalizedArray(item))
    .map((item) => String(item).replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .forEach((item) => {
      const key = normalizedKey(item);
      if (!key || invalidKeys.has(key)) return;
      if (!byKey.has(key)) byKey.set(key, item);
    });
  return Array.from(
    byKey.values()
  )
    .sort((a, b) => a.localeCompare(b))
    .slice(0, 150);
}

function buildFacetsFromRows(rows = []) {
  const states = uniqueSorted(rows.map((item) => item.state));
  return {
    cities: uniqueSorted(rows.map((item) => item.city), { invalidValues: states }),
    states,
    countries: uniqueSorted(rows.flatMap((item) => [item.countries, item.countriesServed])),
    services: uniqueSorted(rows.flatMap((item) => [item.courses, item.services])),
  };
}

function isPostcode(value = "") {
  return /^\d{4,8}$/.test(String(value).trim());
}

function isEmptyLocationValue(value = "") {
  return ["", "-", "na", "n/a", "none", "null", "undefined"].includes(normalizedText(value));
}

function isUsableCity(value, states = []) {
  if (isEmptyLocationValue(value) || isPostcode(value)) return false;
  const key = normalizedKey(value);
  if (!key) return false;
  return !states.some((stateValue) => normalizedKey(stateValue) === key);
}

function citiesForState(rows = [], selectedState = "", states = []) {
  if (!selectedState) return [];
  const selectedStateKey = normalizedKey(selectedState);
  return uniqueSorted(
    rows
      .filter((item) => normalizedKey(item.state) === selectedStateKey)
      .map((item) => item.city)
      .filter((item) => isUsableCity(item, states)),
    { invalidValues: states }
  );
}

function hasFacetOptions(nextFacets = {}) {
  return ["cities", "states", "countries", "services"].some((key) => (nextFacets[key] || []).length > 0);
}

function locallyFilterRows(rows, { search, country, state, city, service, verifiedOnly, claimedOnly, offersOnly }) {
  const query = normalizedText(search);
  return rows.filter((item) => {
    const countries = normalizedArray(item.countriesServed || item.countries);
    const services = normalizedArray(item.services || item.courses);
    const searchable = [
      item.name,
      item.city,
      item.state,
      item.location,
      item.address,
      ...countries,
      ...services,
    ].map(normalizedText).join(" ");
    const isVerified = ["ADMIN_VERIFIED", "OWNER_VERIFIED"].includes(item.verificationLevel) || item.verified;
    const isClaimed = item.claimed || item.claimStatus === "VERIFIED";

    if (query && !searchable.includes(query)) return false;
    if (country && !countries.some((entry) => normalizedText(entry) === normalizedText(country))) return false;
    if (state && normalizedText(item.state) !== normalizedText(state)) return false;
    if (city && normalizedText(item.city) !== normalizedText(city)) return false;
    if (service && !services.some((entry) => normalizedText(entry) === normalizedText(service))) return false;
    if (verifiedOnly && !isVerified) return false;
    if (claimedOnly && !isClaimed) return false;
    if (offersOnly && getActiveOffers(item.offers).length === 0) return false;
    return true;
  });
}

export default function Consultancies() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [consultancies, setConsultancies] = useState([]);
  const [facets, setFacets] = useState({ cities: [], states: [], countries: [], services: [] });
  const [facetRows, setFacetRows] = useState([]);
  const [facetRowsLoaded, setFacetRowsLoaded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [country, setCountry] = useState(searchParams.get("country") || "");
  const [state, setState] = useState(searchParams.get("state") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [service, setService] = useState(searchParams.get("service") || "");
  const [verifiedOnly, setVerifiedOnly] = useState(searchParams.get("verifiedOnly") === "true");
  const [claimedOnly, setClaimedOnly] = useState(searchParams.get("claimedOnly") === "true");
  const [offersOnly, setOffersOnly] = useState(searchParams.get("offersOnly") === "true");
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "recommended");
  const [page, setPage] = useState(Math.max(1, Number(searchParams.get("page") || 1)));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const navigate = useNavigate();
  const activeFilterCount = [country, state, city, service, verifiedOnly, claimedOnly, offersOnly].filter(Boolean).length;
  const cityOptions = citiesForState(facetRows, state, facets.states || []);

  useEffect(() => {
    const fetchFacets = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/consultancies/facets`);
        const contentType = res.headers.get("content-type") || "";
        if (res.ok && contentType.includes("application/json")) {
          const nextFacets = await res.json();
          if (hasFacetOptions(nextFacets)) {
            setFacets({
              cities: uniqueSorted(nextFacets.cities || [], { invalidValues: nextFacets.states || [] }),
              states: uniqueSorted(nextFacets.states || []),
              countries: uniqueSorted(nextFacets.countries || []),
              services: uniqueSorted(nextFacets.services || []),
            });
          }
        }

        const allRows = [];
        let nextPage = 1;
        let lastPage = 1;
        do {
          const params = new URLSearchParams({
            page: String(nextPage),
            limit: String(facetFallbackPageSize),
            sort: "alphabetical",
          });
          const fallbackRes = await fetch(`${BACKEND_URL}/consultancies?${params.toString()}`);
          const fallbackContentType = fallbackRes.headers.get("content-type") || "";
          if (!fallbackRes.ok || !fallbackContentType.includes("application/json")) break;
          const payload = await fallbackRes.json();
          const rows = Array.isArray(payload) ? payload : payload.consultancies || [];
          allRows.push(...rows);
          lastPage = Array.isArray(payload) ? 1 : Math.max(1, Number(payload.totalPages || 1));
          nextPage += 1;
        } while (nextPage <= lastPage);

        setFacetRows(allRows);
        setFacetRowsLoaded(true);
        setFacets(buildFacetsFromRows(allRows));
      } catch (err) {
        console.error("Error fetching consultancy facets", err);
        setFacetRowsLoaded(true);
      }
    };
    fetchFacets();
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileFiltersOpen]);

  useEffect(() => {
    const next = {
      search: searchParams.get("search") || "",
      country: searchParams.get("country") || "",
      state: searchParams.get("state") || "",
      city: searchParams.get("city") || "",
      service: searchParams.get("service") || "",
      verifiedOnly: searchParams.get("verifiedOnly") === "true",
      claimedOnly: searchParams.get("claimedOnly") === "true",
      offersOnly: searchParams.get("offersOnly") === "true",
      sortBy: searchParams.get("sort") || "recommended",
      page: Math.max(1, Number(searchParams.get("page") || 1)),
    };
    setSearch(next.search);
    setCountry(next.country);
    setState(next.state);
    setCity(next.city);
    setService(next.service);
    setVerifiedOnly(next.verifiedOnly);
    setClaimedOnly(next.claimedOnly);
    setOffersOnly(next.offersOnly);
    setSortBy(next.sortBy);
    setPage(next.page);
  }, [searchParams]);

  useEffect(() => {
    const fetchConsultancies = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        [
          ["search", search],
          ["country", country],
          ["state", state],
          ["city", city],
          ["service", service],
          ["sort", sortBy],
          ["page", page],
          ["limit", pageSize],
        ].forEach(([key, value]) => {
          if (value) params.set(key, value);
        });
        if (verifiedOnly) params.set("verifiedOnly", "true");
        if (claimedOnly) params.set("claimedOnly", "true");

        const res = await fetch(`${BACKEND_URL}/consultancies?${params.toString()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`Failed to load consultancies (${res.status})`);
        const data = await res.json();
        const rows = Array.isArray(data) ? data : data.consultancies || [];
        if (Array.isArray(data)) {
          const filteredRows = locallyFilterRows(rows, {
            search,
            country,
            state,
            city,
            service,
            verifiedOnly,
            claimedOnly,
            offersOnly,
          });
          const nextTotalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
          const safePage = Math.min(page, nextTotalPages);
          setConsultancies(filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize));
          setTotalCount(filteredRows.length);
          setTotalPages(nextTotalPages);
        } else {
          const filteredRows = offersOnly ? rows.filter((item) => getActiveOffers(item.offers).length > 0) : rows;
          setConsultancies(filteredRows);
          setTotalCount(Number(data.totalCount || filteredRows.length));
          setTotalPages(Math.max(1, Number(data.totalPages || 1)));
        }
      } catch (err) {
        console.error("Error fetching consultancies", err);
        setConsultancies([]);
        setTotalCount(0);
        setTotalPages(1);
        setError("Unable to load consultancies right now.");
      } finally {
        setLoading(false);
      }
    };

    fetchConsultancies();
  }, [city, claimedOnly, country, offersOnly, page, search, service, sortBy, state, token, verifiedOnly]);

  const setQuery = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    if (
      Object.prototype.hasOwnProperty.call(updates, "state") &&
      normalizedKey(updates.state) !== normalizedKey(state)
    ) {
      next.delete("city");
    }
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, String(value));
      else next.delete(key);
    });
    if (!Object.prototype.hasOwnProperty.call(updates, "page")) next.delete("page");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, state]);

  useEffect(() => {
    if (!city || !facetRowsLoaded) return;
    if (!state || !cityOptions.some((item) => normalizedKey(item) === normalizedKey(city))) {
      setQuery({ city: "" });
    }
  }, [city, cityOptions, facetRowsLoaded, setQuery, state]);

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
  };

  const handleViewDetails = (consultancy) => {
    navigate(`/consultancy/${consultancy._id}`);
  };

  const handleContact = (email) => {
    if (email) window.location.href = `mailto:${email}`;
  };

  const filters = (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm font-semibold text-white">
          <FilterIcon className="h-5 w-5 text-cyan-200" />
          Filters
        </div>
        {activeFilterCount > 0 && (
          <button type="button" onClick={clearFilters} className="text-xs font-semibold text-cyan-200 hover:text-cyan-100">
            Clear
          </button>
        )}
      </div>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Destination</span>
        <select value={country} onChange={(e) => setQuery({ country: e.target.value })} className="mt-2 min-h-[44px] w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-3 text-sm text-white outline-none focus:border-cyan-300">
          <option value="">All countries</option>
          {(facets.countries || []).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">State</span>
        <select value={state} onChange={(e) => setQuery({ state: e.target.value })} className="mt-2 min-h-[44px] w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-3 text-sm text-white outline-none focus:border-cyan-300">
          <option value="">All states</option>
          {(facets.states || []).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">City</span>
        <select
          value={city}
          onChange={(e) => setQuery({ city: e.target.value })}
          disabled={!state}
          className="mt-2 min-h-[44px] w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-3 text-sm text-white outline-none focus:border-cyan-300 disabled:cursor-not-allowed disabled:opacity-55"
        >
          <option value="">{state ? "All cities" : "Select a state first"}</option>
          {cityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        {state && cityOptions.length === 0 && (
          <p className="mt-2 text-xs text-slate-500">No cities available for this state.</p>
        )}
      </label>

      <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[2px] text-slate-500">Service</span>
        <select value={service} onChange={(e) => setQuery({ service: e.target.value })} className="mt-2 min-h-[44px] w-full rounded-lg border border-white/10 bg-slate-900 px-3 py-3 text-sm text-white outline-none focus:border-cyan-300">
          <option value="">All services</option>
          {(facets.services || []).map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </label>

      <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.045] p-3 text-sm text-slate-200">
        <input type="checkbox" checked={verifiedOnly} onChange={(e) => setQuery({ verifiedOnly: e.target.checked ? "true" : "" })} className="h-4 w-4 rounded border-white/20 text-cyan-300 focus:ring-cyan-300" />
        AbroadJacks verified only
      </label>

      <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.045] p-3 text-sm text-slate-200">
        <input type="checkbox" checked={claimedOnly} onChange={(e) => setQuery({ claimedOnly: e.target.checked ? "true" : "" })} className="h-4 w-4 rounded border-white/20 text-cyan-300 focus:ring-cyan-300" />
        Claimed only
      </label>

      <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.045] p-3 text-sm text-slate-200">
        <input type="checkbox" checked={offersOnly} onChange={(e) => setQuery({ offersOnly: e.target.checked ? "true" : "" })} className="h-4 w-4 rounded border-white/20 text-cyan-300 focus:ring-cyan-300" />
        Active offers only
      </label>
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="border-b border-white/10 bg-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:py-14">
          <StatusBadge tone="emerald">
            <ShieldCheckIcon className="mr-1 h-4 w-4" />
            {totalCount || "..."} consultancies indexed
          </StatusBadge>
          <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_380px] lg:items-end">
            <SectionHeader
              eyebrow="Consultancy marketplace"
              title="Discover study abroad consultancies"
              description="Search by country, city, service, or consultancy name, then compare public-source and AbroadJacks verification signals."
            />
            <div className="rounded-lg border border-white/10 bg-white/[0.055] p-3">
              <label className="relative block">
                <span className="sr-only">Search consultancies</span>
                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name, country, city, visa, IELTS..."
                  className="min-h-[48px] w-full rounded-lg border border-white/10 bg-slate-950/80 py-3 pl-11 pr-4 text-sm text-white placeholder-slate-500 outline-none transition focus:border-cyan-300"
                  value={search}
                  onChange={(e) => setQuery({ search: e.target.value })}
                />
              </label>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
          <aside className="hidden h-max rounded-lg border border-white/10 bg-white/[0.04] p-5 lg:block">
            {filters}
          </aside>

          <div className="min-w-0">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
                <StatusBadge tone="cyan">
                  <LocationMarkerIcon className="mr-1 h-4 w-4" />
                  {totalCount.toLocaleString()} results
                </StatusBadge>
                {activeFilterCount > 0 && <span>{activeFilterCount} filters active</span>}
                {error && <span className="text-rose-200">{error}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <button type="button" onClick={() => setMobileFiltersOpen(true)} className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-white lg:hidden">
                  <AdjustmentsIcon className="h-4 w-4" />
                  Filters
                </button>
                <label className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2 text-sm text-slate-300">
                  <TagIcon className="h-4 w-4 text-cyan-200" />
                  <select value={sortBy} onChange={(e) => setQuery({ sort: e.target.value })} className="bg-transparent text-sm font-semibold text-white outline-none">
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-slate-900 text-white">
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {loading ? (
              <LoadingSkeleton rows={4} />
            ) : consultancies.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.04] p-10 text-center">
                <p className="text-lg font-semibold text-white">No consultancies match this search</p>
                <p className="mt-2 text-sm text-slate-400">Try removing filters or searching by destination, city, course, or service.</p>
                <button type="button" onClick={clearFilters} className="mt-5 rounded-lg bg-cyan-300 px-4 py-2 text-sm font-semibold text-slate-950">
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 2xl:grid-cols-3">
                  {consultancies.map((consultancy) => {
                    const topOffer = getActiveOffers(consultancy.offers)[0] || null;
                    return (
                      <ConsultancyCard
                        key={consultancy._id}
                        consultancy={{ ...consultancy, topOffer }}
                        onView={() => handleViewDetails(consultancy)}
                        onContact={() => handleContact(consultancy.email)}
                      />
                    );
                  })}
                </div>
                <div className="mt-8 flex flex-col gap-3 rounded-lg border border-white/10 bg-white/[0.04] p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-300">
                    Page {page} of {totalPages}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:flex">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setQuery({ page: page - 1 })}
                      className="min-h-[44px] rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() => setQuery({ page: page + 1 })}
                      className="min-h-[44px] rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-slate-950 px-4 py-6 text-sm text-slate-400 sm:px-6">
        <div className="mx-auto max-w-7xl">
          Consultancy information is compiled from publicly available business information and/or information supplied by consultancy owners. Independently verify admissions, fees, visa, scholarship, and university-partnership claims before making decisions.
        </div>
      </section>

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm lg:hidden" onClick={() => setMobileFiltersOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Consultancy filters"
            className="absolute inset-x-0 bottom-0 max-h-[86svh] overflow-auto rounded-t-2xl border border-white/10 bg-slate-950 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl sm:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <p className="text-lg font-semibold">Refine results</p>
              <button type="button" aria-label="Close filters" onClick={() => setMobileFiltersOpen(false)} className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/15 text-white">
                <XIcon className="h-5 w-5" />
              </button>
            </div>
            {filters}
            <button type="button" onClick={() => setMobileFiltersOpen(false)} className="mt-6 w-full rounded-lg bg-cyan-300 px-4 py-3 font-semibold text-slate-950">
              Show Results
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
